import { CouponDTO, IAppointmentDetails } from '@/components/fakeDoor/types';
import {
    DEFAULT_MULTIPLE_COUPON_VALUE,
    DEFAULT_PACKAGE_AMOUNT,
    DEFAULT_TOTAL_AMOUNT,
} from '@/constants/index';
import { APPOINTMENT_CARD_TYPE, ServiceTypeEnum } from '@/constants/enums';
import { Coupon, ExtractRewardResponseDto } from '@/types';
import { fetchPackagePricing } from '@/components/fakeDoor/api';
import { extractRewardDetails } from './api';
import { ServiceTypeConfig } from '@/config/rewardServiceTypes';

export function getRewardDetailsFromCoupons(coupons: Coupon[]) {
    if (!Array.isArray(coupons) || coupons.length === 0) {
        return {
            isCouponAvailable: false,
            couponCode: null,
            offerDetails: null,
        };
    }

    if (coupons.length === 1) {
        const coupon = coupons[0];
        return {
            isCouponAvailable: true,
            couponCode: coupon?.coupon_code ?? null,
            offerDetails: {
                offer_title: coupon?.title ?? null,
                offer_description: coupon?.description ?? null,
            },
        };
    }

    return {
        isCouponAvailable: true,
        couponCode: DEFAULT_MULTIPLE_COUPON_VALUE,
        offerDetails: DEFAULT_MULTIPLE_COUPON_VALUE,
    };
}

export function getRewardDetailsFromNotes(coupon_details: CouponDTO) {
    if (!coupon_details || !coupon_details.coupon_id) {
        return {
            isCouponAvailable: false,
            couponCode: null,
            offerDetails: null,
        };
    }

    return {
        isCouponAvailable: true,
        couponCode: coupon_details.coupon_code ?? null,
        offerDetails: {
            offer_title: coupon_details.title ?? null,
            offer_description: coupon_details.description ?? null,
        },
    };
}

export async function getExtractRewardDetailsFromAppointment(
    appointmentData: IAppointmentDetails
) {
    const { model_id, fuel_type } = appointmentData.vehicle_details;
    const garage_id = appointmentData.garage_id;
    const service_type = appointmentData.service_type;
    const utm_medium = appointmentData.source;

    let packageAmount = DEFAULT_TOTAL_AMOUNT;

    if (
        service_type ===
        ServiceTypeConfig[ServiceTypeEnum.PERIODIC_SERVICE].toUpperCase()
    ) {
        try {
            const pricingDetails = await fetchPackagePricing(
                model_id,
                fuel_type,
                garage_id
            );
            if (
                pricingDetails != null &&
                Array.isArray(pricingDetails.package_pricing)
            ) {
                const enhancedPackages = pricingDetails.package_pricing.filter(
                    (pkg: any) => pkg.name === 'ENHANCED'
                );
                if (enhancedPackages.length > 0) {
                    packageAmount = enhancedPackages[0].offer_price ?? 0;
                } else {
                    console.log(
                        'No enhanced package found, fallback amount applied'
                    );
                    packageAmount = DEFAULT_PACKAGE_AMOUNT;
                }
            }
        } catch {
            console.log('Error fetcing package pricing information');
            packageAmount = DEFAULT_PACKAGE_AMOUNT;
        }
    } else {
        packageAmount = DEFAULT_TOTAL_AMOUNT;
    }

    const applyData = {
        garage_id: garage_id,
        service_type: service_type.toLowerCase(),
        utm_medium: utm_medium,
        package_name:
            service_type ===
            ServiceTypeConfig[ServiceTypeEnum.PERIODIC_SERVICE].toUpperCase()
                ? 'enhanced'
                : '',
        total_amount: packageAmount,
        package_amount: packageAmount,
    };

    const response = await extractRewardDetails(applyData);
    if (response.coupons.length === 0) {
        return {
            isCouponAvailable: false,
            couponId: null,
            couponCode: null,
            offerDetails: null,
        };
    }
    return {
        isCouponAvailable: true,
        couponId: response.coupons[0].coupon_id,
        couponCode: response.coupons[0].coupon_code,
        offerDetails: {
            offer_title: response.coupons[0].title,
            offer_description: response.coupons[0].description,
        },
    };
}

export function getMatchingCouponsByServiceType(
    coupons: Coupon[] | undefined,
    rewardServiceType: string
): Coupon[] {
    if (!Array.isArray(coupons)) {
        return [];
    }
    return coupons.filter(
        (coupon: Coupon) =>
            (coupon?.meta?.service_type ?? '') === rewardServiceType
    );
}

export function hasServiceCoupons(
    serviceImage: any,
    rewardDetails: ExtractRewardResponseDto | null | undefined
): boolean {
    if (!serviceImage?.key) {
        return false;
    }

    const matchingCoupons = getMatchingCouponsByServiceType(
        rewardDetails?.coupons,
        serviceImage?.key
    );

    const hasCoupons = matchingCoupons && matchingCoupons.length > 0;

    return !!hasCoupons;
}

export function sortServicesByCouponAvailability(
    servicesImages: any[],
    rewardDetails: ExtractRewardResponseDto | null | undefined
): any[] {
    if (!Array.isArray(servicesImages) || servicesImages.length === 0) {
        return servicesImages;
    }

    const servicesWithCoupons: any[] = [];
    const servicesWithoutCoupons: any[] = [];

    servicesImages.forEach((serviceImage) => {
        if (hasServiceCoupons(serviceImage, rewardDetails)) {
            serviceImage.isCouponAvailable = true;
            servicesWithCoupons.push(serviceImage);
        } else {
            serviceImage.isCouponAvailable = false;
            servicesWithoutCoupons.push(serviceImage);
        }
    });

    return [...servicesWithCoupons, ...servicesWithoutCoupons];
}

export function getRewardValue(
    serviceImage: any,
    rewardDetails: ExtractRewardResponseDto
) {
    if (!rewardDetails || !rewardDetails.coupons) {
        return null;
    }
    const coupon = rewardDetails.coupons.find(
        (coupon: Coupon) => coupon.meta?.service_type === serviceImage.key
    );
    return coupon?.meta?.reward_value ?? null;
}
